import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useConfirm } from '@/data/context/ConfirmModalContext';
import usePdfMaterialDraft, {
  RunInEditableLesson,
  UploadedPdf,
} from '@/data/hook/book/usePdfMaterialDraft';
import { useEditableLesson } from '@/data/hook/lesson/useEditableLesson';
import useLessonReplacement from '@/data/hook/lesson/useLessonReplacement';
import { useSingleCache } from '@/data/hook/query/v2/useCacheManager';
import { updateBook } from '@/data/services/bookServices';
import { booksQueryKeys, lessonsQueryKeys } from '@/data/services/querykeys';
import Book, { BookStyleEnum, bookType } from '@/models/Book';
import Lesson from '@/models/Lesson';
import { toLessonBookPatch } from '@/utils/book/lessonBookPatch';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';

import PdfMaterialForm, { PdfMaterialFormFields } from './PdfMaterialForm';

type PdfMaterialFormServiceProps = {
  lessonId: number;
  bookType: bookType;
  book?: Book;
  onClose: () => void;
};

export default function PdfMaterialFormService({
  lessonId,
  bookType,
  book: savedBook,
  onClose,
}: PdfMaterialFormServiceProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.materialsTab.pdfForm',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { runEditable } = useEditableLesson(lessonId);
  const { replaceLesson } = useLessonReplacement();
  const lessonCache = useSingleCache<Lesson>(
    lessonsQueryKeys.get(lessonId).queryKey,
  );
  const runInDraft: RunInEditableLesson = action =>
    runEditable(action, { skipOnVersioned: true });
  const draft = usePdfMaterialDraft({
    bookType,
    book: savedBook,
    runEditable: runInDraft,
  });
  const { uploaded, attachment } = draft;

  const finish = (resultLessonId?: number) => {
    if (resultLessonId && resultLessonId !== lessonId)
      return replaceLesson(resultLessonId);
    onClose();
  };

  const handleSubmit = ({ title }: PdfMaterialFormFields) => {
    const bookId = uploaded?.book.id ?? savedBook?.id;
    if (!bookId) throw new Error('Pdf not uploaded');
    if (uploaded && title === uploaded.book.filename)
      return Promise.resolve(uploaded);
    return runInDraft<UploadedPdf>(async editableLessonId => {
      const book = await updateBook(
        { lessonId: editableLessonId, bookType, bookId },
        { filename: title },
      );
      return { lessonId: editableLessonId, book };
    });
  };

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: handleSubmit,
    onError: error => alert.error(getErrorMessage(error)),
    onSuccess: async ({ lessonId: savedLessonId, book }) => {
      queryClient.setQueryData(booksQueryKeys.get(book.id).queryKey, book);
      alert.success(t('saveSuccess'));
      if (savedLessonId === lessonId)
        lessonCache.merge(
          toLessonBookPatch(bookType, {
            id: book.id,
            bookStyle: BookStyleEnum.PDF_INTERACTIVE,
          }),
        );
      finish(savedLessonId);
    },
  });

  const handleCancel = async () => {
    draft.cancel();
    try {
      await draft.discardDraft();
      finish(uploaded?.lessonId);
    } catch (error) {
      alert.error(getErrorMessage(error));
    }
  };

  const handleRemoveFile = () => {
    if (!attachment) return;
    confirm({
      title: t('removeFileConfirm.title'),
      description: t('removeFileConfirm.description', {
        name: attachment.name,
      }),
      confirmLabel: t('removeFile'),
      cancelLabel: tCommon('cancel'),
      variant: 'destructive',
      onConfirm: draft.removeAttachment,
      onError: error => alert.error(getErrorMessage(error)),
    });
  };

  return (
    <PdfMaterialForm
      draft={draft}
      defaultTitle={savedBook?.filename}
      onSubmit={onSubmit}
      onCancel={handleCancel}
      onRemoveFile={handleRemoveFile}
      isLoading={isPending}
    />
  );
}
